import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  storePhoneCode,
} from '../../../utils/helper';
import { getIsViewIsEdit, PageType } from '../../../utils/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppleButtonLogo from '../../../../assets/icons/appleButtonLogo';
import {
  setCheckoutErrorMessage,
  setPurchaseCompleted,
  setSubmitSuccess,
} from '../../../redux/slices/commonSlice';
import {
  setIsDeclinePopup,
  setDeclineReasonCode,
  setPurchaseToken,
  setCheckoutOrderID,
} from '../../../redux/slices/checkoutSlice';
import { EventPublisher } from '../../../utils/publishEvent';
import { v4 as uuidv4 } from 'uuid';
import { handleEventLogger } from '../../../utils/conversionAPI';
import { setCartData } from '../../../redux/slices/cartSlice';
import { resetState } from '../../../redux/slices/postUpsellStateSlice';
import { IErrorResponse } from '../../../interface/apiInterface';
import { Cart } from '../../../interface/checkoutInterface';
import { startApplePay } from './paymentHelper';
import checkoutService from '../../../api/checkoutService';
import '../../text-editor/style.scss';
import { CheckoutCompletePayload } from '../../../interface/apiServiceInterface';
import { postUpsellPage } from '../../../utils/postUpsell';
import { loadExternalScript } from '../../../utils/loadExternalScript';
import { useFormContext } from 'react-hook-form';
import { AddressFormType } from '../../../interface/formInterface';
import { MarginType } from '../../../puck/reusable-props/margin';
import { PaddingType } from '../../../puck/reusable-props/padding';
import { WidthAndHeightSubProps } from '../../../puck/reusable-props/widthAndHeight';
import { IBorderValueProps } from '../../../puck/reusable-props/Border';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';

type ApplePayButtonProps = {
  margin: MarginType;
  padding: PaddingType;
  isDark: boolean;
  editMode?: boolean;
  showOnlyButton: boolean;
  buttonCustomization: WidthAndHeightSubProps;
  headerText: string;
  justifyContent: string;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  TermsAndConCheck?: { isTermsAndConChecked: boolean; errorMsg: string };
} & ReduxSelectorProps &
  ReduxDispatchProps;

const ApplePayButtonRender: React.FC<ApplePayButtonProps> = ({
  margin,
  padding,
  isDark,
  showOnlyButton,
  buttonCustomization,
  headerText,
  justifyContent,
  border,
  background,
  TermsAndConCheck,
  useAppDispatch,
  useAppSelector,
  editMode,
}) => {
  const dispatch = useAppDispatch();
  const { setValue } = useFormContext<AddressFormType>();
  const [searchParams] = useSearchParams();
  const cartState = useAppSelector(state => state.cart);
  const { pageId, currentPageFunnelId } = useAppSelector(
    state => state.checkout,
  );
  const setCartDataRef = useRef<Cart | null>(null);
  const cartData = useAppSelector(state => state.cart?.cartData);
  const [isAppleDevice, setIsAppleDevice] = useState(false);
  const [isAppleSDKLoaded, setIsAppleSDKLoaded] = useState(false);
  const storeData = useAppSelector(state => state?.store?.storeData);
  const windowWidth = useAppSelector(state => state.store.currentWidth);
  const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
  const upsellData = useAppSelector(state => state.postUpSell.upsellData);
  const postUpSellProducts = useAppSelector(
    state => state.postUpSell.postUpsellData,
  );
  const navigate = useNavigate();
  const newSearchParams = new URLSearchParams(searchParams.toString());
  const postUpsellRedirectUrl = `/post-upsell/?${newSearchParams.toString()}`;
  const Apple = cartState.ApplePay;
  const eventToken = searchParams.get('event-token');
  const previewStoreID = searchParams.get('storeID');
  const igParams = searchParams.get('igParams');
  const marginStyles = getMarginStyles(margin, currentDeviceType);
  const publish = EventPublisher.getInstance().publish;
  const { isTermsAccepted, IsEmailConsent, IsSMSConsent } = useAppSelector(
    state => state.common,
  );
  const [termsError, setTermsError] = useState<string | null>(null);
  const smartFunnelId = useAppSelector(
    state => state.postUpSell.smartUpsellFunnelID,
  );
  const NextPageConfig = useAppSelector(state => state.checkout.nextpageConfig);
  const isCartApiLoading = useAppSelector(state => state.cart.isCartApiLoading);

  const navState = useMemo(() => {
    if (!getIsViewIsEdit() && NextPageConfig?.id) {
      return {
        state: {
          id: NextPageConfig.id,
          pageVisiblity: NextPageConfig.pageVisiblity,
          pageType: NextPageConfig.pageType,
          smartFunnelId:
            NextPageConfig.pageType === PageType?.UPSELL ? smartFunnelId : '',
        },
        replace: true,
      };
    }
    return {};
  }, [
    NextPageConfig?.id,
    NextPageConfig?.pageVisiblity,
    NextPageConfig?.pageType,
    getIsViewIsEdit,
  ]);

  const handleNavigate = () => {
    navigate('/' + '?' + newSearchParams.toString(), navState);
  };

  const completeApi = async (
    payload: CheckoutCompletePayload,
    session: ApplePaySession,
  ) => {
    dispatch(setCheckoutErrorMessage(null));
    dispatch(setIsDeclinePopup(false));
    if (!cartData?.ID || !storeData?.StoreID || !storeData?.ShopID) return;
    if (!getIsViewIsEdit()) {
      publish('PurchaseAttempt', {
        eventId: uuidv4(),
        eventName: 'PurchaseAttempt',
        shopId: storeData?.ShopID,
        cart: cartData,
        paymentType: 'Apple Pay',
      });
      publish('AddPaymentInfo', {
        eventId: uuidv4(),
        eventName: 'AddPaymentInfo',
        shopId: storeData?.ShopID,
        cart: cartData,
      });
    }
    const updatedCart = {
      ...cartData,
      Email: setCartDataRef.current?.Email || cartData.Email,
      ShippingAddress:
        setCartDataRef.current?.ShippingAddress || cartData.ShippingAddress,
      PhoneNumber: setCartDataRef.current?.PhoneNumber || cartData.PhoneNumber,
      BillingAddress: payload.BillingAddress,
      TotalAmount: setCartDataRef.current?.TotalAmount || cartData.TotalAmount,
      Fees: setCartDataRef.current?.Fees || cartData.Fees,
      SubtotalAmount:
        setCartDataRef.current?.SubtotalAmount || cartData.SubtotalAmount,
      SalesTaxAmount:
        setCartDataRef.current?.SalesTaxAmount || cartData.SalesTaxAmount,
    };
    dispatch(setSubmitSuccess(true));
    try {
      const uniqueEventId = uuidv4();
      const response = await checkoutService.checkoutComplete(
        payload,
        uniqueEventId,
      );
      if (!getIsViewIsEdit()) {
        publish('Purchase', {
          eventId: uniqueEventId,
          eventName: 'Purchase',
          shopId: storeData?.ShopID,
          cart: updatedCart,
          orderId: response?.data?.PurchaseToken,
          status: response?.status,
        });
        // this event is moved to backend so commenting it out for now once every this is working fine we can remove this
        // if (CallMetaApi) {
        //   handleCapi({
        //     eventName: 'Purchase',
        //     ShopID: storeData.ShopID,
        //     Cart: cartData,
        //     orderId: response?.data?.PurchaseToken,
        //   });
        // }

        handleEventLogger({
          eventType: 'payment',
          shopifyDetails: {
            shopId: storeData?.ShopID,
          },
          apiDetails: {
            payload,
            response: response.data,
            message: 'Payment Successfull',
          },
          eventToken: eventToken || null,
          cartDetails: cartData,
        });
      }
      dispatch(
        setCartData({
          ...cartData,
          Email: setCartDataRef.current?.Email || cartData.Email,
          ShippingAddress:
            setCartDataRef.current?.ShippingAddress || cartData.ShippingAddress,
          PhoneNumber:
            setCartDataRef.current?.PhoneNumber || cartData.PhoneNumber,
          BillingAddress: payload.BillingAddress,
        }),
      );
      dispatch(setPurchaseToken(response.data?.PurchaseToken));
      dispatch(setCheckoutOrderID(response.data?.OrderID));
      dispatch(resetState());
      dispatch(setPurchaseCompleted(true));
      storePhoneCode('+1');
      let upselldataParsed = '';
      try {
        upselldataParsed = JSON.stringify(upsellData || '{}');
      } catch (error) {
        console.log('Error parsing upsellData:', error);
      }

      if (
        postUpsellPage?.includes(storeData?.ShopID) &&
        postUpSellProducts &&
        postUpSellProducts.length > 0
      ) {
        navigate(postUpsellRedirectUrl);
      } else if (upsellData && upselldataParsed !== '{}') {
        handleNavigate();
      } else {
        dispatch(resetState());
        dispatch(setPurchaseCompleted(true));
        storePhoneCode('+1');
      }
    } catch (e) {
      const error = e as IErrorResponse;
      handleEventLogger({
        eventType: 'payment',
        shopifyDetails: {
          shopId: storeData?.ShopID,
        },
        apiDetails: {
          payload,
          response: error,
          message: 'Payment Failed',
        },
        cartDetails: cartData,
        eventToken: eventToken || null,
      });
      const errorMessage = error?.error?.response?.data?.ProcessMessage
        ? error?.error?.response?.data?.Msg
        : '';
      if (errorMessage) {
        dispatch(setCheckoutErrorMessage(errorMessage));
      } else if (error?.status === 402) {
        dispatch(setIsDeclinePopup(true));
        dispatch(
          setDeclineReasonCode(error?.message || 'Invalid card information'),
        );

        dispatch(setSubmitSuccess(false));
      } else if (
        error?.status === 403 &&
        error?.message?.toLowerCase().includes('fraud')
      ) {
        dispatch(setCheckoutErrorMessage(`Order can not be completed`));
      } else {
        dispatch(
          setCheckoutErrorMessage(
            error?.message ||
              `We encountered an error. Please try again. ${error?.status}`,
          ),
        );
      }
      if (error.status) {
        dispatch(setSubmitSuccess(false));
      }
    } finally {
      session.abort();
      dispatch(setSubmitSuccess(false));
    }
  };

  useEffect(() => {
    if (termsError && isTermsAccepted) {
      setTermsError(null);
    }
  }, [isTermsAccepted]);

  useEffect(() => {
    const checkDeviceSupport = async () => {
      const userAgent = navigator?.userAgent || '';
      const isNonApple = /(Windows|Android|Linux|Unix)/i.test(userAgent);
      if (isNonApple && !getIsViewIsEdit()) {
        setIsAppleDevice(false);
        setIsAppleSDKLoaded(false);
        return;
      }
      setIsAppleSDKLoaded(false);
      setIsAppleDevice(false);
      try {
        console.log('[ApplePayButton] start loading Apple Pay SDK');
        await loadExternalScript(
          'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js',
          'apple-pay-script',
        );
        console.log('[ApplePayButton] Apple Pay SDK loaded successfully');
        console.log(window?.ApplePaySession, 'window.ApplePaySession');

        const merchantIdentifier = 'merchant.phoenix.app';
        if (!window?.ApplePaySession) return;
        setIsAppleSDKLoaded(true);
        if (!window?.ApplePaySession?.canMakePayments) return;
        const canMakePayments = window?.ApplePaySession.canMakePayments();
        if (!canMakePayments) return;

        console.log('[ApplePayButton] canMakePayments:', canMakePayments);
        if (
          typeof window?.ApplePaySession?.applePayCapabilities === 'function'
        ) {
          console.log('[ApplePayButton] applePayCapabilities is available');
        } else {
          console.log('[ApplePayButton] applePayCapabilities is not available');
        }
        console.log('[ApplePayButton] Checking Apple Pay capabilities...');

        let capabilities;
        setTimeout(async () => {
          console.log('[ApplePayButton] setTimeout triggered Start');
          capabilities =
            await window?.ApplePaySession?.applePayCapabilities(
              merchantIdentifier,
            );
          if (
            ['paymentCredentialStatusUnknown', 'applePayUnsupported'].includes(
              capabilities?.paymentCredentialStatus || '',
            )
          ) {
            return;
          }
          setIsAppleDevice(true);
          console.log('[ApplePayButton] setTimeout triggered End');
        }, 0);
      } catch (error) {
        console.error('Failed to load Apple Pay SDK:', error);
      }
    };

    checkDeviceSupport();
  }, []);

  if ((!+Apple?.IsApplePay || !isAppleDevice) && !getIsViewIsEdit()) {
    return <></>;
  }

  const containerStyle = {
    ...marginStyles,
    backgroundColor: background?.isInherit ? 'inherit' : background?.color,
    ...getPaddingStyles(padding, currentDeviceType),
    ...getBorderProps(border, currentDeviceType),
  };
  const isDisabled = TermsAndConCheck?.isTermsAndConChecked && !isTermsAccepted;
  const handlePreviewApplePayClick = async () => {
    if (isDisabled) {
      setTermsError(
        TermsAndConCheck?.errorMsg || 'Please accept the terms and conditions.',
      );
      return;
    }
    setTermsError(null);

    if (!isAppleSDKLoaded) {
      try {
        await loadExternalScript(
          'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js',
          'apple-pay-script',
        );
        setIsAppleSDKLoaded(true);
      } catch (error) {
        console.error('Apple Pay SDK failed to laod on click', error);
        return;
      }
    }
    if (!window?.ApplePaySession || !cartData) return;
    startApplePay(
      Apple,
      {
        ...cartData,
        StoreID: previewStoreID || cartData.StoreID,
      },
      completeApi,
      setCartDataRef,
      storeData,
      isTermsAccepted,
      dispatch,
      setValue,
      IsSMSConsent,
      IsEmailConsent,
      igParams,
      pageId,
      currentPageFunnelId,
    );
  };
  const buttonstyle: React.CSSProperties =
    currentDeviceType &&
    buttonCustomization?.maxWidth &&
    buttonCustomization?.height &&
    buttonCustomization?.maxWidth?.[currentDeviceType] !== undefined &&
    buttonCustomization?.height?.[currentDeviceType] !== undefined
      ? {
          width: isNaN(+buttonCustomization?.maxWidth?.[currentDeviceType])
            ? buttonCustomization?.maxWidth?.[currentDeviceType]
            : `${+buttonCustomization?.maxWidth?.[currentDeviceType]}px`,
          height: isNaN(+buttonCustomization?.height?.[currentDeviceType])
            ? buttonCustomization?.height?.[currentDeviceType]
            : `${+buttonCustomization?.height?.[currentDeviceType]}px`,
        }
      : {};

  const buttonClasses = `w-fit rounded-md border-2 ${isDark ? (isDisabled ? 'bg-gray-600' : 'bg-black') : isDisabled ? 'bg-gray-400' : 'bg-white'} ${justifyContent}`;
  const buttonLoadingClasses = `w-fit rounded-md bg-gray-200 ${justifyContent} animate-pulse`;

  if (showOnlyButton) {
    return (
      <div className="flex flex-col " style={containerStyle}>
        <button
          className={!isAppleSDKLoaded ? buttonLoadingClasses : buttonClasses}
          type="button"
          onClick={handlePreviewApplePayClick}
          disabled={!isAppleSDKLoaded}
          style={{
            ...buttonstyle,
          }}>
          {!isAppleSDKLoaded ? (
            <div className="flex items-center justify-center gap-2 px-4 py-2" />
          ) : (
            <AppleButtonLogo isDark={isDark} width="100%" height="100%" />
          )}
        </button>
        {termsError && (
          <p className={`text-red-500 text-sm mt-2 ${justifyContent}`}>
            {termsError}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col space-y-4 px-6 py-8 w-full text-editor-content"
      style={{ ...containerStyle }}>
      <div dangerouslySetInnerHTML={{ __html: headerText }} />
      <button
        className={!isAppleSDKLoaded ? buttonLoadingClasses : buttonClasses}
        style={{
          ...buttonstyle,
        }}
        type="button"
        disabled={!isAppleSDKLoaded || isCartApiLoading}
        onClick={handlePreviewApplePayClick}>
        {!isAppleSDKLoaded ? (
          <div className="flex items-center justify-center gap-2 px-4 py-2" />
        ) : (
          <AppleButtonLogo isDark={isDark} width="100%" height="100%" />
        )}
      </button>
      {termsError && (
        <p className={`text-red-500 text-sm mt-2 ${justifyContent}`}>
          {termsError}
        </p>
      )}
      <div className="flex items-center w-full">
        <hr className="flex-grow border-t border-gray-300" />
        <span className="mx-2 text-gray-500 text-sm whitespace-nowrap">OR</span>
        <hr className="flex-grow border-t border-gray-300" />
      </div>
    </div>
  );
};

export default ApplePayButtonRender;
